import { inject } from "mobx-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { isPWSSessionBackend, PWSSessionState, COAST_MESSAGES } from "@inferno/renderer-shared-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { PageBlockInterface } from "../../../../core/page-blocks/Block.component";
import { Button, ButtonKind, Container, FauxButton, Heading } from "../../../../core/ui";
import Spinner from "../../../../core/ui/Spinner.component";
import { emailPattern, FORM_ERRORS, SimpleInput, validateEmailsMatch } from "../../components/forms";
import { RecaptchaForm, RecaptchaFromOnSubmit } from "../../components/forms/RecaptchaForm.component";
import { TextAreaInput } from "../../components/forms/TextAreaInput.component";
import { useCoastAlerts } from "../../components/user/common/coast-alert/CoastAlerts.hook";
import { CoastAlertsDisplay } from "../../components/user/common/coast-alert/CoastAlertsDisplay.components";
import "./CoastContact.style.scss";
import type { Store } from "@inferno/renderer-shared-core";
import { sendEmail } from "../../../../core/services/PostUp";

const log = ILog.logger("CoastContact.component");

interface FormFields {
  name: string;
  email: string;
  confirmEmail: string;
  subject: string;
  message: string;
}

interface CoastContactProps extends PageBlockInterface {
  store?: Store;
}

const CoastContact = inject("store")(({ store }: CoastContactProps) => {
  const session = store?.session.currentSession;

  if (!store || !isPWSSessionBackend(session)) {
    return null;
  }

  const { alerts, setErrorAlert, clearAllAlerts, setSuccessAlert, removeAlert } = useCoastAlerts();
  const [isAuthenticated, setIsAuthenticated] = useState(session.authenticated);
  const [isProcessing, setIsProcessing] = useState(false);
  const loginErrorRef = useRef<string | null>(null);

  const formMethods = useForm<FormFields>();
  const { register, errors, watch: watchForm, reset: resetForm } = formMethods;

  const base_uri = "/api/v4";
  const preSubmit = useCallback(() => {
    clearAllAlerts();
    setIsProcessing(true);
  }, [clearAllAlerts]);
  const onSubmit: RecaptchaFromOnSubmit<FormFields> = useCallback(
    async ({ email, name, message, subject }, recaptchaResponse) => {
      try {
        store.onAnalyticsAction.dispatch({
          sectionName: "email_us",
          pageName: "",
          context: "submit",
          action: "click",
        });

        const emailVariables = {
          slug: store.site.index.slug,
          input: {
            senderIP: "n/a", // set on server for now
            senderEmail: email,
            senderName: name,
            message,
            subject,
            recipientType: "Email George",
            replyDesired: false,
          },
        };

        const data = await sendEmail({ variables: emailVariables, base_uri, recaptchaResponse });

        if (!data) {
          setErrorAlert(COAST_MESSAGES.UNEXPECTED_ERROR);
        } else {
          setSuccessAlert(COAST_MESSAGES.EMAIL_GEORGE_SUCCESS);
        }
      } catch (e) {
        log.error(e.message);
        resetForm();
        setErrorAlert(COAST_MESSAGES.UNEXPECTED_ERROR);
      } finally {
        setIsProcessing(false);
      }
    },
    [resetForm, setErrorAlert, setSuccessAlert, store.onAnalyticsAction, store.site.index.slug],
  );

  const handleLoginAlert = useCallback(
    (loggedIn: boolean) => {
      if (loggedIn) {
        if (loginErrorRef.current) {
          removeAlert(loginErrorRef.current);
          loginErrorRef.current = null;
        }
      } else if (!loginErrorRef.current) {
        loginErrorRef.current = setErrorAlert(COAST_MESSAGES.EMAIL_GEORGE_LOGIN);
      }
    },
    [removeAlert, setErrorAlert],
  );

  const handleSessionChange = useCallback(
    (state: PWSSessionState) => {
      handleLoginAlert(state.authenticated);

      if (state.authenticated) {
        if (!isAuthenticated) {
          setIsAuthenticated(true);
        }
      } else if (isAuthenticated) {
        setIsAuthenticated(false);
      }
    },
    [handleLoginAlert, isAuthenticated],
  );

  useEffect(() => {
    session.onStatusChanged.subscribe(handleSessionChange);

    return () => session.onStatusChanged.unsubscribe(handleSessionChange);
  }, [handleSessionChange, session.onStatusChanged]);

  useEffect(() => {
    if (!isAuthenticated) {
      session.showUsersPopup();
      handleLoginAlert(isAuthenticated);
    }
  }, [handleLoginAlert, isAuthenticated, session]);

  return (
    <Container className="component-coast-contact">
      <Heading level={2}>George Noory Private E-mail</Heading>

      <p>
        As a Coast Insider member, use this e-mail form to send your thoughts, ideas and responses to George. These
        e-mails do get preferential treatment in preparing the program each night.
      </p>
      <p>
        The heavy volume of email we get precludes a personal response, but listen to Coast to Coast AM, as these
        e-mails are sometimes read and responded to by George during the program.
      </p>
      <p>
        If you need to send attachments, email them separately to&nbsp;
        <a href="mailto:george@coasttocoastam.com" target="_blank" rel="noreferrer">
          george@coasttocoastam.com
        </a>
        . If you are having technical or billing subscription problems, please visit our&nbsp;
        <FauxButton to="https://help.coasttocoastam.com/" kind={ButtonKind.CUSTOMIZED}>
          Customer Service section
        </FauxButton>
        .
      </p>

      <FormProvider {...formMethods}>
        <RecaptchaForm
          siteKey={store.env.GOOGLE_RECAPTCHA_V3_PUBLIC_KEY}
          setIsProcessing={setIsProcessing}
          setError={setErrorAlert}
          preSubmit={preSubmit}
          onSubmit={onSubmit}
          action="signup"
        >
          <SimpleInput
            placeholder="Jane Doe"
            rules={{ required: FORM_ERRORS.required }}
            error={errors.name}
            register={register}
            label="Full Name"
            name="name"
            type="text"
          />

          <SimpleInput
            placeholder="email@email.com"
            rules={{ required: FORM_ERRORS.required, pattern: emailPattern }}
            error={errors.email}
            register={register}
            label="Your Email"
            name="email"
            type="email"
          />

          <SimpleInput
            placeholder="email@email.com"
            rules={{
              required: FORM_ERRORS.required,
              validate: v => validateEmailsMatch(watchForm("email"), v),
            }}
            error={errors.confirmEmail}
            register={register}
            label="Confirm Email"
            name="confirmEmail"
            type="email"
          />

          <SimpleInput
            placeholder="Your subject"
            rules={{ required: FORM_ERRORS.required }}
            error={errors.subject}
            register={register}
            label="Subject"
            name="subject"
            type="text"
          />

          <TextAreaInput
            placeholder="Enter your message here"
            rules={{ required: FORM_ERRORS.required }}
            error={errors.subject}
            register={register}
            required={true}
            minLength={10}
            label="Message"
            name="message"
            rows={10}
          />

          <CoastAlertsDisplay alerts={alerts} />

          <Button
            disabled={!!Object.keys(errors).length || !isAuthenticated || isProcessing}
            label="Submit"
            type="submit"
          >
            {isProcessing ? <Spinner visible={true} /> : "Submit"}
          </Button>
        </RecaptchaForm>
      </FormProvider>
    </Container>
  );
});

export default CoastContact;
